/**
 * `RegionMap` — OSM-карта с маркерами СНП (FE-13).
 *
 * V2_TZ §1.9 + §5.7:
 *  - OSM tiles (без API-key);
 *  - маркеры из `/api/public/kato/coordinates` (BE-14); СНП без валидных
 *    координат отсечены на BE, FE рендерит всё, что пришло;
 *  - popup с показателями СНП и кнопками переходов (RegionInfoCard, redesign);
 *  - центрирование — по центру масс координат;
 *  - клик на маркер → попап + выбор КАТО в контексте (FE-15);
 *  - `boundary` — граница выбранной единицы (Nominatim, см. osm.request):
 *    рисуется GeoJSON-слоем;
 *  - `focus` — куда лететь камере: bbox границы / bbox точек поддерева
 *    (область, район, сельский округ) или точка СНП (без границы).
 */
import L from 'leaflet';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GeoJSON,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  ZoomControl,
  useMap,
  useMapEvents,
} from 'react-leaflet';

import { useKato } from '../../contexts/KatoContext';
import type { OsmBoundary } from '../../requests/osm.request';
import { RegionInfoCard } from './RegionInfoCard';

import 'leaflet/dist/leaflet.css';

import type { KatoCoordinate } from '../../types/public';

// CRA + leaflet 1.9 — пути к маркерам не подхватываются автоматически;
// прописываем default-icon вручную.
L.Marker.prototype.options.icon = L.icon({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const brandIcon = L.divIcon({
  className: '',
  html: '<span class="block size-3 rounded-full bg-brand-500 ring-2 ring-white shadow"></span>',
  iconSize: [12, 12],
});

const KAZAKHSTAN_CENTER: [number, number] = [48.0196, 66.9237];

/**
 * Клик по карте — зум на ступень к точке клика. Клики по маркерам сюда
 * не доходят (у L.Marker bubblingMouseEvents=false по умолчанию).
 */
const ZoomOnClick = () => {
  const map = useMapEvents({
    click: (event) => {
      map.setZoomAround(event.latlng, map.getZoom() + 1);
    },
  });
  return null;
};

/**
 * Контейнер карты меняет размер после mount (шапка/сайдбар рендерятся
 * позже инициализации leaflet) — без invalidateSize тайлы грузятся только
 * в исходном прямоугольнике.
 */
const InvalidateOnResize = () => {
  const map = useMap();
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map]);
  return null;
};

/** Куда подлетать камере при выборе КАТО. */
export type MapFocus =
  | { kind: 'point'; center: [number, number]; zoom?: number }
  | { kind: 'bounds'; bounds: [[number, number], [number, number]] };

/** Зум для одиночного СНП (точка без границы). */
const POINT_FOCUS_ZOOM = 13;

/** Подлетает к выбранной цели: bbox единицы или точка СНП. */
const FocusLayer = ({ focus }: { focus: MapFocus }) => {
  const map = useMap();
  useEffect(() => {
    // flyTo при контейнере нулевого размера (StrictMode/HMR-ремоунт) даёт
    // NaN в анимации и зум в максимум — ждём, пока у карты появится
    // реальный размер, и на всякий случай ограничиваем зум.
    let timer: number | undefined;
    const fly = () => {
      const size = map.getSize();
      if (size.x === 0 || size.y === 0) {
        map.invalidateSize();
        timer = window.setTimeout(fly, 150);
        return;
      }
      try {
        if (focus.kind === 'point') {
          map.flyTo(focus.center, focus.zoom ?? POINT_FOCUS_ZOOM, {
            duration: 0.9,
          });
        } else {
          map.flyToBounds(focus.bounds, {
            padding: [48, 48],
            duration: 0.9,
            maxZoom: 12,
          });
        }
      } catch (err) {
        console.error('FocusLayer zoom failed', err);
      }
    };
    fly();
    return () => window.clearTimeout(timer);
  }, [map, focus]);
  return null;
};

/** Рисует границу выбранной единицы (зум — отдельно, через FocusLayer). */
const BoundaryLayer = ({ boundary }: { boundary: OsmBoundary }) => (
  // key: react-leaflet не обновляет data GeoJSON-слоя — пересоздаём слой.
  <GeoJSON
    key={JSON.stringify(boundary.bbox)}
    data={boundary.geometry}
    style={{
      color: '#2E9E45',
      weight: 2.5,
      fillColor: '#34C24A',
      fillOpacity: 0.08,
    }}
  />
);

export const centerOfMass = (
  coords: KatoCoordinate[],
): [number, number] => {
  if (coords.length === 0) return KAZAKHSTAN_CENTER;
  const sum = coords.reduce(
    (acc, c) => ({ lat: acc.lat + c.latitude, lon: acc.lon + c.longitude }),
    { lat: 0, lon: 0 },
  );
  return [sum.lat / coords.length, sum.lon / coords.length];
};

interface Props {
  coordinates: KatoCoordinate[];
  /** Граница выбранной единицы (null — не выбрана / в OSM нет полигона). */
  boundary?: OsmBoundary | null;
  /** Цель зума при выборе КАТО (null — камеру не трогаем). */
  focus?: MapFocus | null;
  /**
   * Клик по маркеру (до setSelectedKato) — главная по нему подавляет
   * оверлей-карточку: у маркера информация уже открывается в leaflet-попапе.
   */
  onMarkerSelect?: (kato: number) => void;
  /** Высота карты — карта в leaflet требует фиксированную высоту. */
  height?: string | number;
}

export const RegionMap = ({
  coordinates,
  boundary = null,
  focus = null,
  onMarkerSelect,
  height = '600px',
}: Props) => {
  const { i18n } = useTranslation();
  const { setSelectedKato } = useKato();
  const isKz = i18n.language === 'kz';

  const center = useMemo(() => centerOfMass(coordinates), [coordinates]);
  // Зум по умолчанию 7 — область целиком. С полным набором ЗКО — нормальный
  // обзор; точная подстройка bounds — отдельная задача (нужен fitBounds после mount).
  const zoom = coordinates.length === 0 ? 5 : 7;

  return (
    <div data-testid="region-map" className="h-full w-full" style={{ height }}>
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom
        zoomControl={false}
        style={{ height: '100%', width: '100%' }}
      >
        {/* Зум — справа внизу: левый верх занят чипами фильтров. */}
        <ZoomControl position="bottomright" />
        <InvalidateOnResize />
        <ZoomOnClick />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {boundary && <BoundaryLayer boundary={boundary} />}
        {focus && <FocusLayer focus={focus} />}
        {coordinates.map((coord) => (
          <Marker
            key={coord.kato}
            position={[coord.latitude, coord.longitude]}
            icon={brandIcon}
            eventHandlers={{
              // Клик открывает попап (навигация — кнопками внутри попапа);
              // выбранный КАТО подхватывает сайдбар.
              click: () => {
                onMarkerSelect?.(coord.kato);
                setSelectedKato(coord.kato);
              },
            }}
          >
            <Popup maxWidth={360}>
              <RegionInfoCard
                kato={coord.kato}
                name={isKz ? coord.name_kz : coord.name_ru}
                className="w-[300px] p-4 shadow-none"
              />
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};
